import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, onSnapshot, updateDoc, increment,
  collection, query, orderBy, where,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useLocation as useGeoLocation } from '../../contexts/LocationContext';
import {
  MapPin, Phone, Clock, Navigation, ArrowLeft,
  Share2, Heart, AlertTriangle, CalendarClock, Info, User, Tag, Timer,
  Users, CheckCircle2, ShoppingBag, MessageCircle, Package,
} from 'lucide-react';
import MapView from '../map/MapView';
import Loading from '../common/Loading';
import { useFavorites, useRecentViews } from '../../hooks/useLocalStorage';
import { ScheduleDetail } from '../common/ScheduleDisplay';
import { useShopPack } from '../../hooks/useShopPack';
import ProductQuickViewClient from './ProductQuickViewClient';

/* ── Libellés types de commerce ─────────────────────────── */
const COMMERCE_TYPE_LABELS = {
  boulangerie:    'Boulangerie',
  epicerie:       'Épicerie',
  boucherie:      'Boucherie',
  pharmacie:      'Pharmacie',
  cafe:           'Café / Snack',
  restaurant:     'Restaurant',
  coiffeur:       'Coiffeur',
  vetements:      'Vêtements',
  electronique:   'Électronique',
  librairie:      'Librairie',
  fruits_legumes: 'Fruits & Légumes',
  autre:          'Autre',
};

/* ── helpers ─────────────────────────────────────────────── */
const toNum = v => { const n = parseFloat(v); return isNaN(n) ? null : n; };

const formatDate = ts => {
  if (!ts) return 'Inconnue';
  try {
    const d   = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return 'Inconnue';
    const min = Math.floor((Date.now() - d.getTime()) / 60000);
    if (min < 1)    return "A l'instant";
    if (min < 60)   return `Il y a ${min} min`;
    if (min < 1440) return `Il y a ${Math.floor(min / 60)}h`;
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  } catch { return 'Inconnue'; }
};

const formatCountdownDetailed = (ts) => {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    if (isNaN(d.getTime())) return null;
    const diff = d.getTime() - Date.now();
    if (diff <= 0) return null;
    const totalSec = Math.ceil(diff / 1000);
    const min      = Math.floor(totalSec / 60);
    const sec      = totalSec % 60;
    if (min < 1) return `${sec}s`;
    if (min < 60) return `${min} min ${String(sec).padStart(2, '0')}s`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m > 0 ? `${h}h ${String(m).padStart(2, '0')}min` : `${h}h`;
  } catch { return null; }
};

const heroGradient = s => {
  if (s === 'open')      return 'linear-gradient(145deg, #00b396, #00c9a7)';
  if (s === 'uncertain') return 'linear-gradient(145deg, #d97706, #f5a623)';
  if (s === 'be_back')   return 'linear-gradient(145deg, #1d4ed8, #3b82f6)';
  return 'linear-gradient(145deg, #b91c1c, #f0564a)';
};

const statusLabel = (s, beBackExpired) => {
  if (s === 'open')      return 'Ouvert maintenant';
  if (s === 'uncertain') return 'Statut incertain';
  if (s === 'be_back')   return beBackExpired ? 'Censé revenir bientôt' : 'De retour bientôt';
  return 'Fermé';
};

/* ══════════════════════════════════════════════════════════ */
const ShopDetail = () => {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const [shop, setShop]                   = useState(null);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [countdown, setCountdown]         = useState(null);
  const [beBackExpired, setBeBackExpired] = useState(false);
  const [waitingDone, setWaitingDone]     = useState(false);
  const [waitingLoading, setWaitingLoading] = useState(false);
  const [activeTab,   setActiveTab  ]     = useState('infos');
  const [products,    setProducts   ]     = useState([]);
  const [categories,  setCategories ]     = useState([]);
  const [activeCat,   setActiveCat  ]     = useState(null);
  const [quickViewProduct, setQuickViewProduct] = useState(null);

  const { isFavorite, toggleFavorite } = useFavorites();
  const { addRecent }                  = useRecentViews();
  const { location, calculateDistance } = useGeoLocation();
  const packInfo                        = useShopPack(shop);

  /* ── Vérifie si le client a déjà signalé son attente ── */
  useEffect(() => {
    if (!id) return;
    const key = `yeldi_waiting_${id}`;
    if (localStorage.getItem(key)) setWaitingDone(true);
  }, [id]);

  /* ── Chargement produits + catégories ── */
  useEffect(() => {
    if (!id) return;
    const qCat  = query(
      collection(db, 'shops', id, 'categories'),
      where('status', '==', 'published'),
      orderBy('createdAt', 'asc')
    );
    const qProd = query(
      collection(db, 'shops', id, 'products'),
      where('status', '==', 'published'),
      orderBy('createdAt', 'desc')
    );
    const u1 = onSnapshot(qCat,  snap => setCategories(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    const u2 = onSnapshot(qProd, snap => setProducts(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
    return () => { u1(); u2(); };
  }, [id]);

  /* ── Chargement shop ── */
  useEffect(() => {
    if (!id) { setLoading(false); setError('Identifiant manquant.'); return; }
    const unsub = onSnapshot(
      doc(db, 'shops', id),
      snap => {
        if (snap.exists()) { const d = { id: snap.id, ...snap.data() }; setShop(d); addRecent(d); }
        else setError("Ce magasin n'existe pas ou a ete supprime.");
        setLoading(false);
      },
      err => { console.error(err); setError('Impossible de charger ce magasin.'); setLoading(false); }
    );
    return () => unsub();
  }, [id]); // eslint-disable-line

  /* ── Compte à rebours live ── */
  useEffect(() => {
    if (shop?.status !== 'be_back' || !shop?.returnAt) {
      setCountdown(null);
      setBeBackExpired(false);
      return;
    }
    const tick = () => {
      const cd = formatCountdownDetailed(shop.returnAt);
      setCountdown(cd);
      setBeBackExpired(!cd);
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [shop?.status, shop?.returnAt]);

  /* ── Signaler son attente ── */
  const handleWaiting = useCallback(async () => {
    if (!shop || waitingDone || waitingLoading) return;
    if (location) {
      const shopLat = toNum(shop.latitude);
      const shopLng = toNum(shop.longitude);
      if (shopLat && shopLng) {
        const dist = calculateDistance(location.lat, location.lng, shopLat, shopLng);
        if (dist > 0.5) return;
      }
    }
    setWaitingLoading(true);
    try {
      await updateDoc(doc(db, 'shops', shop.id), { waitingCount: increment(1) });
      localStorage.setItem(`yeldi_waiting_${shop.id}`, '1');
      setWaitingDone(true);
    } catch (err) {
      console.error('handleWaiting error:', err);
    } finally {
      setWaitingLoading(false);
    }
  }, [shop, waitingDone, waitingLoading, location, calculateDistance]);

  const getDirections = () => {
    const lat = toNum(shop?.latitude);
    const lng = toNum(shop?.longitude);
    if (!lat || !lng) return;
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank', 'noopener,noreferrer');
  };

  const share = async () => {
    try {
      if (navigator.share) await navigator.share({ title: shop.name, text: shop.address, url: window.location.href });
      else await navigator.clipboard.writeText(window.location.href);
    } catch (e) { if (e?.name !== 'AbortError') console.warn(e); }
  };

  if (loading) return <Loading message="Chargement du magasin…"/>;

  if (error || !shop) return (
    <div style={{ padding: '3rem 2rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
      <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--bg)', boxShadow: 'var(--neu)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)' }}>
        <MapPin size={28} strokeWidth={1.5}/>
      </div>
      <h3 style={{ fontWeight: 800 }}>Magasin introuvable</h3>
      <p style={{ color: 'var(--text-2)', fontSize: '0.875rem', maxWidth: 240, lineHeight: 1.5 }}>
        {error || "Ce magasin n'existe plus."}
      </p>
      <button className="btn btn-primary" onClick={() => navigate('/')}>
        <ArrowLeft size={16}/> Retour a l'accueil
      </button>
    </div>
  );

  const lat       = toNum(shop.latitude);
  const lng       = toNum(shop.longitude);
  const hasCoords = lat !== null && lng !== null;
  const fav       = isFavorite(shop.id);
  const isOpen    = shop.status === 'open';
  const isUncert  = shop.status === 'uncertain';
  const isBeBack  = shop.status === 'be_back';

  const returnAtTime = (() => {
    if (!shop.returnAt) return null;
    try {
      const d = shop.returnAt?.toDate ? shop.returnAt.toDate() : new Date(shop.returnAt);
      if (isNaN(d.getTime())) return null;
      return `${d.getHours()}h${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return null; }
  })();

  return (
    <div style={{ paddingBottom: '2rem' }}>

      {/* ── Hero ── */}
      <div style={{
        background: heroGradient(shop.status),
        padding: '1rem 1.1rem 3.5rem',
        color: '#fff',
        position: 'inherit',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.35rem' }}>
          <button onClick={() => navigate(-1)} aria-label="Retour" style={circleBtn}>
            <ArrowLeft size={19}/>
          </button>
          <div style={{ display: 'flex', gap: '0.45rem' }}>
            <button
              onClick={() => toggleFavorite(shop.id)}
              aria-label={fav ? "Retirer des favoris" : "Ajouter aux favoris"}
              style={{ ...circleBtn, background: fav ? "rgba(255,255,255,.95)" : "transparent", color: fav ? "#ef4444" : "#fff" }}
            >
              <Heart size={17} color={fav ? "#ef4444" : "#fff"} fill={fav ? "#ef4444" : "none"}/>
            </button>
            <button onClick={share} aria-label="Partager" style={circleBtn}>
              <Share2 size={17}/>
            </button>
          </div>
        </div>

        <h1 style={{ fontSize: '1.55rem', fontWeight: 800, lineHeight: 1.2, marginBottom: '0.75rem', letterSpacing: '-0.4px' }}>
          {shop.name}
        </h1>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', alignItems: 'center' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
            background: 'rgba(255,255,255,.22)', backdropFilter: 'blur(6px)',
            borderRadius: '999px', padding: '0.38rem 1rem',
            fontWeight: 700, fontSize: '0.85rem', color: '#fff',
            border: '1.5px solid rgba(255,255,255,.40)',
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%', background: '#fff', flexShrink: 0,
              animation: (isOpen || isBeBack) ? 'pulseDot 2.2s ease infinite' : 'none',
            }}/>
            {statusLabel(shop.status, beBackExpired)}
          </div>

          {shop.managedBy === 'schedule' && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,.88)',
              background: 'rgba(255,255,255,.15)', borderRadius: '999px',
              padding: '0.28rem 0.65rem', border: '1px solid rgba(255,255,255,.28)',
            }}>
              <CalendarClock size={11} strokeWidth={2}/>
              Horaires auto
            </div>
          )}

          {isBeBack && countdown && !beBackExpired && (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              fontSize: '0.72rem', fontWeight: 700, color: '#fff',
              background: 'rgba(255,255,255,.18)', borderRadius: '999px',
              padding: '0.28rem 0.75rem', border: '1px solid rgba(255,255,255,.30)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <Timer size={11} strokeWidth={2.5}/>{countdown}
            </div>
          )}
        </div>

        <div style={{ fontSize: '0.75rem', opacity: 0.72, marginTop: '0.55rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Clock size={11}/>
          Mis a jour {formatDate(shop.lastSeenAt || shop.updatedAt)}
        </div>
      </div>

      {/* ── Onglets Infos / Boutique ── */}
      {products.length > 0 && (
        <div style={{
          display: 'flex', background: 'var(--bg)',
          borderBottom: '1px solid var(--outline)',
          position: 'sticky', top: 0, zIndex: 10,
          marginTop: '-2.2rem',
        }}>
          {[
            { key: 'infos',    label: 'Infos',    icon: MapPin },
            { key: 'boutique', label: 'Boutique',  icon: ShoppingBag, badge: products.length },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                flex: 1, padding: '0.75rem 0.5rem',
                background: 'none', border: 'none',
                borderBottom: `2.5px solid ${activeTab === t.key ? 'var(--primary)' : 'transparent'}`,
                color: activeTab === t.key ? 'var(--primary)' : 'var(--text-2)',
                fontWeight: 700, fontSize: '0.83rem',
                cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                gap: '0.35rem', transition: 'color 0.15s',
              }}
            >
              <t.icon size={14} strokeWidth={2}/>
              {t.label}
              {t.badge > 0 && (
                <span style={{
                  background: activeTab === t.key ? 'var(--primary)' : 'var(--bg-elevated)',
                  color: activeTab === t.key ? '#fff' : 'var(--text-3)',
                  borderRadius: '999px', padding: '0.05rem 0.45rem',
                  fontSize: '0.62rem', fontWeight: 700,
                }}>
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* ════════════════════════════════════════════ */}
      {/* ── ONGLET BOUTIQUE ── */}
      {/* ════════════════════════════════════════════ */}
      {activeTab === 'boutique' && (
        <div style={{ padding: '0.75rem 1rem', paddingBottom: '2rem' }}>

          {/* Bouton WhatsApp (pack premium/pro) */}
          {packInfo.hasWhatsApp && shop.whatsapp && (
            <a
              href={`https://wa.me/${shop.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Bonjour, je vous contacte depuis Yeldi — ${shop.name}`)}`}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: '0.6rem',
                padding: '0.75rem 1rem', marginBottom: '0.85rem',
                borderRadius: 'var(--r-lg)', textDecoration: 'none',
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                color: '#fff', fontWeight: 700, fontSize: '0.88rem',
                boxShadow: '0 3px 12px rgba(34,197,94,0.35)',
              }}
            >
              <MessageCircle size={18} strokeWidth={2}/>
              Contacter sur WhatsApp
            </a>
          )}

          {/* Filtre catégories */}
          {categories.length > 0 && (
            <div style={{
              display: 'flex', gap: '0.4rem', overflowX: 'auto',
              paddingBottom: '0.5rem', marginBottom: '0.75rem',
              scrollbarWidth: 'none',
            }}>
              <button
                onClick={() => setActiveCat(null)}
                style={{
                  padding: '0.35rem 0.85rem', borderRadius: '999px', border: 'none',
                  background: !activeCat ? 'var(--primary)' : 'var(--bg-elevated)',
                  color: !activeCat ? '#fff' : 'var(--text-2)',
                  fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer',
                  fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                  boxShadow: !activeCat ? '0 2px 8px rgba(0,201,167,0.30)' : 'var(--neu-sm)',
                }}
              >
                Tous
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCat(cat.id === activeCat ? null : cat.id)}
                  style={{
                    padding: '0.35rem 0.85rem', borderRadius: '999px', border: 'none',
                    background: activeCat === cat.id ? 'var(--primary)' : 'var(--bg-elevated)',
                    color: activeCat === cat.id ? '#fff' : 'var(--text-2)',
                    fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer',
                    fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0,
                    boxShadow: activeCat === cat.id ? '0 2px 8px rgba(0,201,167,0.30)' : 'var(--neu-sm)',
                  }}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          )}

          {/* ── Grille produits ── */}
          {(() => {
            const filtered = activeCat
              ? products.filter(p => p.categoryId === activeCat)
              : products;

            if (filtered.length === 0) return (
              <div style={{ textAlign: 'center', padding: '2rem 1rem' }}>
                <div style={{ fontSize: '1.8rem', marginBottom: '0.6rem' }}>📦</div>
                <div style={{ fontWeight: 700, color: 'var(--text-2)', fontSize: '0.85rem' }}>
                  Aucun produit dans cette catégorie
                </div>
              </div>
            );

            return (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.65rem' }}>
                {filtered.map(product => {
                  const cat = categories.find(c => c.id === product.categoryId);
                  return (
                    <div
                      key={product.id}
                      onClick={() => setQuickViewProduct(product)}
                      style={{
                        background: 'var(--bg)',
                        borderRadius: 'var(--r-lg)',
                        boxShadow: 'var(--neu-sm)',
                        overflow: 'hidden',
                        display: 'flex', flexDirection: 'column',
                        cursor: 'pointer',
                        transition: 'transform 0.15s, box-shadow 0.15s',
                        WebkitTapHighlightColor: 'transparent',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = 'var(--neu)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'var(--neu-sm)'; }}
                    >
                      {/* Image */}
                      <div style={{
                        width: '100%', aspectRatio: '1 / 1',
                        background: 'var(--bg-elevated)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        overflow: 'hidden', position: 'relative',
                      }}>
                        {product.images?.[0] ? (
                          <>
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              loading="lazy"
                            />
                            {/* Indicateur plusieurs photos */}
                            {product.images.length > 1 && (
                              <div style={{
                                position: 'absolute', top: 6, right: 6,
                                background: 'rgba(0,0,0,0.5)',
                                borderRadius: 6, padding: '2px 6px',
                                fontSize: 9, fontWeight: 700, color: '#fff',
                                backdropFilter: 'blur(4px)',
                              }}>
                                1/{product.images.length}
                              </div>
                            )}
                          </>
                        ) : (
                          <Package size={28} color="var(--text-3)" strokeWidth={1.5}/>
                        )}

                        {/* Overlay "Voir" au hover */}
                        <div style={{
                          position: 'absolute', inset: 0,
                          background: 'rgba(0,0,0,0)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'background 0.2s',
                        }} className="prod-overlay"/>
                      </div>

                      {/* Infos */}
                      <div style={{ padding: '0.55rem 0.65rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
                        <div style={{
                          fontWeight: 700, fontSize: '0.82rem',
                          color: 'var(--text)', lineHeight: 1.25,
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          {product.name}
                        </div>

                        {product.description && (
                          <div style={{
                            fontSize: '0.7rem', color: 'var(--text-3)',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}>
                            {product.description}
                          </div>
                        )}

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '0.3rem' }}>
                          {product.price != null && product.showPrice !== false ? (
                            <span style={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--primary)' }}>
                              {product.price.toLocaleString()} DA
                            </span>
                          ) : <span/>}

                          {cat && (
                            <span style={{
                              fontSize: '0.62rem', fontWeight: 600,
                              color: 'var(--text-3)',
                              background: 'var(--bg-elevated)',
                              borderRadius: '999px', padding: '0.1rem 0.4rem',
                              maxWidth: 80, overflow: 'hidden',
                              textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}>
                              {cat.name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── ONGLET INFOS ── */}
      {activeTab === 'infos' && (
        <div style={{ marginTop: products.length > 0 ? '0' : '-2.2rem', padding: '0 1rem', display: 'flex', flexDirection: 'column', gap: '0.8rem', paddingTop: products.length > 0 ? '0.75rem' : '0' }}>

          {/* Card infos principales */}
          <div className="card" style={{ margin: 0, padding: 0, overflow: 'hidden' }}>
            <InfoRow icon={<MapPin size={16} color="var(--primary)"/>} label="Adresse">
              <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)', lineHeight: 1.4 }}>
                {shop.address || '—'}
              </span>
            </InfoRow>

            {shop.commerceType && (
              <>
                <div className="divider"/>
                <InfoRow icon={<Tag size={16} color="var(--primary)"/>} label="Type de commerce">
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>
                    {COMMERCE_TYPE_LABELS[shop.commerceType] || shop.commerceType.replace('_', ' ')}
                  </span>
                </InfoRow>
              </>
            )}

            {shop.showOwner && shop.ownerName && (
              <>
                <div className="divider"/>
                <InfoRow icon={<User size={16} color="var(--primary)"/>} label="Propriétaire">
                  <span style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--text)' }}>
                    {shop.ownerName}
                  </span>
                </InfoRow>
              </>
            )}

            {shop.phone && shop.showPhone !== false && (
              <>
                <div className="divider"/>
                <InfoRow icon={<Phone size={16} color="var(--primary)"/>} label="Telephone">
                  <a href={`tel:${shop.phone}`} style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--primary)', textDecoration: 'none', letterSpacing: '0.3px' }}>
                    {shop.phone}
                  </a>
                </InfoRow>
              </>
            )}

            {shop.openHours && (
              <>
                <div className="divider"/>
                <InfoRow icon={<Clock size={16} color="var(--primary)"/>} label="Horaires" alignStart>
                  <ScheduleDetail openHours={shop.openHours}/>
                </InfoRow>
              </>
            )}
          </div>

          {/* Bannière be_back actif */}
          {isBeBack && !beBackExpired && (
            <div style={{
              borderRadius: 'var(--r-lg)', background: 'var(--bg)', boxShadow: 'var(--neu-sm)',
              padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.85rem',
              border: '1.5px solid rgba(59,130,246,0.20)',
            }}>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                <div style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', background: 'rgba(59,130,246,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Timer size={20} color="#3b82f6" strokeWidth={2}/>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#1d4ed8', marginBottom: '0.25rem' }}>Vous pouvez attendre !</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-2)', lineHeight: 1.55 }}>Le propriétaire sera de retour dans peu de temps.</div>
                </div>
              </div>

              {countdown && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(59,130,246,0.12))',
                  borderRadius: 'var(--r-lg)', padding: '0.9rem 1rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  border: '1px solid rgba(59,130,246,0.18)',
                }}>
                  <div>
                    <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.2rem' }}>Retour estimé dans</div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#1d4ed8', fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>{countdown}</div>
                    {returnAtTime && <div style={{ fontSize: '0.72rem', color: 'var(--text-3)', marginTop: '0.2rem', fontWeight: 500 }}>Prévu à {returnAtTime}</div>}
                  </div>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', border: '3px solid rgba(59,130,246,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Timer size={22} color="#3b82f6" strokeWidth={2}/>
                  </div>
                </div>
              )}

              {waitingDone ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.75rem 1rem', borderRadius: 'var(--r-lg)', background: 'rgba(59,130,246,0.08)', border: '1.5px solid rgba(59,130,246,0.22)' }}>
                  <CheckCircle2 size={18} color="#3b82f6" strokeWidth={2} style={{ flexShrink: 0 }}/>
                  <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#2563eb', lineHeight: 1.4 }}>Le propriétaire est averti que vous l'attendez</span>
                </div>
              ) : (
                <button onClick={handleWaiting} disabled={waitingLoading} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.8rem 1rem', borderRadius: 'var(--r-lg)', border: '2px solid rgba(59,130,246,0.35)', background: waitingLoading ? 'var(--bg-elevated)' : 'rgba(59,130,246,0.10)', color: '#2563eb', fontWeight: 700, fontSize: '0.9rem', cursor: waitingLoading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', transition: 'all 0.18s', width: '100%' }}>
                  {waitingLoading ? <div style={{ width: 15, height: 15, border: '2px solid rgba(37,99,235,0.3)', borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }}/> : <Users size={17} strokeWidth={2}/>}
                  {waitingLoading ? 'Envoi…' : 'Attendre ?'}
                </button>
              )}

              {shop.phone ? (
                <a href={`tel:${shop.phone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.72rem', borderRadius: 'var(--r-lg)', background: 'linear-gradient(145deg, #4f7bf5, #1d4ed8)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', boxShadow: '3px 3px 10px rgba(59,130,246,.35)' }}>
                  <Phone size={15}/>Contacter le propriétaire
                </a>
              ) : (
                <div className="info-inline"><Info size={13} style={{ flexShrink: 0, marginTop: 1, color: 'var(--text-3)' }}/><span>Aucun numéro disponible — patientez ou repassez un peu plus tard.</span></div>
              )}
            </div>
          )}

          {/* Bannière be_back expiré */}
          {isBeBack && beBackExpired && (
            <div style={{ borderRadius: 'var(--r-lg)', background: 'var(--bg)', boxShadow: 'var(--neu-sm)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--warning-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={17} color="var(--warning)" strokeWidth={2}/>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--text)', marginBottom: '0.25rem' }}>
                    Censé revenir{returnAtTime && <span style={{ fontWeight: 600, color: 'var(--warning)', marginLeft: '0.35rem' }}>depuis {returnAtTime}</span>}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-2)', lineHeight: 1.55 }}>Le propriétaire avait signalé son retour mais le délai est dépassé.</div>
                </div>
              </div>
              {shop.phone ? (
                <a href={`tel:${shop.phone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.72rem', borderRadius: 'var(--r-lg)', background: 'linear-gradient(145deg, #f5b83d, #e8920f)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', boxShadow: '3px 3px 10px rgba(245,166,35,.40)' }}>
                  <Phone size={15}/>Appeler pour vérifier
                </a>
              ) : (
                <div className="info-inline"><Info size={13} style={{ flexShrink: 0, marginTop: 1, color: 'var(--text-3)' }}/><span>Aucun numéro disponible — repassez plus tard.</span></div>
              )}
            </div>
          )}

          {/* Bannière statut incertain */}
          {isUncert && (
            <div style={{ borderRadius: 'var(--r-lg)', background: 'var(--bg)', boxShadow: 'var(--neu-sm)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'var(--warning-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AlertTriangle size={17} color="var(--warning)" strokeWidth={2}/>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Statut non confirmé</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-2)', lineHeight: 1.55 }}>Le commercant n'a pas confirmé son statut recemment. Il a peut-etre quitte son magasin sans mettre a jour l'application.</div>
                </div>
              </div>
              {shop.phone ? (
                <a href={`tel:${shop.phone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.72rem', borderRadius: 'var(--r-lg)', background: 'linear-gradient(145deg, #f5b83d, #e8920f)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', boxShadow: '3px 3px 10px rgba(245,166,35,.40)' }}>
                  <Phone size={15}/>Appeler pour verifier
                </a>
              ) : (
                <div className="info-inline"><Info size={13} style={{ flexShrink: 0, marginTop: 1, color: 'var(--text-3)' }}/><span>Aucun numero disponible — repassez plus tard ou consultez les horaires.</span></div>
              )}
            </div>
          )}

          {/* Bannière oublié d'ouvrir */}
          {shop.mightBeForgotten && shop.status === 'closed' && (
            <div style={{ borderRadius: 'var(--r-lg)', background: 'var(--bg)', boxShadow: 'var(--neu-sm)', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', background: 'rgba(59,130,246,.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Clock size={17} color="#3b82f6" strokeWidth={2}/>
                </div>
                <div>
                  <div style={{ fontWeight: 800, fontSize: '0.88rem', color: 'var(--text)', marginBottom: '0.25rem' }}>Ce magasin devrait etre ouvert</div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-2)', lineHeight: 1.55 }}>Selon ses horaires, il devrait etre ouvert maintenant. Le commercant n'a peut-etre pas encore mis a jour l'application ce matin.</div>
                </div>
              </div>
              {shop.phone ? (
                <a href={`tel:${shop.phone}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', padding: '0.72rem', borderRadius: 'var(--r-lg)', background: 'linear-gradient(145deg, #4f7bf5, #1d4ed8)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', textDecoration: 'none', boxShadow: '3px 3px 10px rgba(59,130,246,.40)' }}>
                  <Phone size={15}/>Appeler pour verifier
                </a>
              ) : (
                <div className="info-inline"><Info size={13} style={{ flexShrink: 0, marginTop: 1, color: 'var(--text-3)' }}/><span>Aucun numero disponible — repassez plus tard.</span></div>
              )}
            </div>
          )}

          {/* CTA itinéraire */}
          <button className="btn btn-primary btn-large" onClick={getDirections} disabled={!hasCoords} style={{ margin: 0 }}>
            <Navigation size={19}/>
            Obtenir l'itineraire
          </button>

          {!hasCoords && (
            <div className="alert alert-warning" style={{ margin: 0 }}>
              <MapPin size={16} style={{ flexShrink: 0 }}/>
              <span>Coordonnees GPS non disponibles pour ce magasin.</span>
            </div>
          )}

          {/* Mini carte */}
          {hasCoords && (
            <div style={{ height: 220, borderRadius: 'var(--r-xl)', overflow: 'hidden', boxShadow: 'var(--neu)' }}>
              <MapView shops={[{ ...shop, latitude: lat, longitude: lng }]} center={[lat, lng]} zoom={16}/>
            </div>
          )}
        </div>
      )}

      {/* ── Modal Quick View Produit (client) ── */}
      {quickViewProduct && (
        <ProductQuickViewClient
          product={quickViewProduct}
          category={categories.find(c => c.id === quickViewProduct.categoryId)}
          shop={shop}
          packInfo={packInfo}
          onClose={() => setQuickViewProduct(null)}
        />
      )}

    </div>
  );
};

/* ── Styles partagés ── */
const circleBtn = {
  background: 'rgba(255,255,255,.22)',
  backdropFilter: 'blur(6px)',
  border: '1px solid rgba(255,255,255,.30)',
  borderRadius: '50%',
  width: 40, height: 40,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#fff',
  transition: 'all 0.2s', flexShrink: 0,
};

const InfoRow = ({ icon, label, children, alignStart = false }) => (
  <div style={{ display: 'flex', gap: '0.85rem', alignItems: alignStart ? 'flex-start' : 'center', padding: '0.9rem 1rem' }}>
    <div style={{ width: 38, height: 38, borderRadius: 'var(--r-sm)', background: 'var(--bg)', boxShadow: 'var(--neu-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: alignStart ? 2 : 0 }}>
      {icon}
    </div>
    <div style={{ minWidth: 0, flex: 1 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '0.22rem' }}>
        {label}
      </div>
      {children}
    </div>
  </div>
);

export default ShopDetail;